import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.store';
import { selectAuthToken } from 'src/app/store/selectors/auth.selectors';
import { updateToken, logout } from 'src/app/store/actions/auth.actions';
import { Observable, of, throwError, BehaviorSubject, combineLatest } from 'rxjs';
import { switchMap, take, catchError, tap, map } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private tokenReadySubject = new BehaviorSubject<boolean>(false);
  private orgIdReadySubject = new BehaviorSubject<boolean>(false);
  tokenReady$ = this.tokenReadySubject.asObservable();
  orgIdReady$ = this.orgIdReadySubject.asObservable();
  authReady$ = combineLatest([this.tokenReady$, this.orgIdReady$]).pipe(
    map(([tokenReady, orgIdReady]) => tokenReady && orgIdReady)
  );

  constructor(
    private auth: AuthService,
    private store: Store<AppState>,
    private router: Router
  ) {
    // Initialize ready states
    this.initializeReadyStates();
  }

  private initializeReadyStates(): void {
    // Check token readiness
    const authData = JSON.parse(localStorage.getItem('auth') || '{}');
    if (authData.token) {
      try {
        const decoded = jwtDecode<any>(authData.token);
        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded.exp && decoded.exp > currentTime) {
          this.tokenReadySubject.next(true);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    // Check org_id readiness
    const orgId = localStorage.getItem('org_id');
    if (orgId) {
      this.orgIdReadySubject.next(true);
    }
  }

  /**
   * Gets a token with fallback strategy:
   * 1. First try from store
   * 2. Then try from localStorage
   * 3. Finally, try from Auth0 getAccessTokenSilently
   */
  getTokenWithFallback(): Observable<string> {
    return this.store.select(selectAuthToken).pipe(
      take(1),
      switchMap(token => {
        if (token) {
          this.tokenReadySubject.next(true);
          return of(token);
        }
        
        // Next try from localStorage
        try {
          const authData = JSON.parse(localStorage.getItem('auth') || '{}');
          if (authData.token) {
            // Check if token is expired
            const currentTime = Math.floor(Date.now() / 1000);
            try {
              const decoded = jwtDecode<any>(authData.token);
              if (decoded.exp && decoded.exp > currentTime) {
                this.tokenReadySubject.next(true);
                return of(authData.token);
              }
            } catch (error) {
              console.error('Error decoding token:', error);
            }
          }
        } catch (error) {
          console.error('Error getting token from localStorage:', error);
        }
        
        // Fallback to Auth0
        return this.auth.getAccessTokenSilently().pipe(
          take(1),
          switchMap(newToken => {
            // Update the token in the store
            if (newToken) {
              this.store.dispatch(updateToken({ token: newToken }));
              this.tokenReadySubject.next(true);
            }
            return of(newToken);
          }),
          catchError(error => {
            this.tokenReadySubject.next(false);
            // Check for Auth0 specific refresh token error
            if (this.isRefreshTokenError(error)) {
              console.error('Invalid refresh token detected. Logging out user:', error);
              this.handleLogout();
              return throwError(() => new Error('Session expired. Please login again.'));
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  /**
   * Set org_id and mark it as ready
   */
  setOrgId(orgId: string): void {
    localStorage.setItem('org_id', orgId);
    this.orgIdReadySubject.next(true);
  }

  /**
   * Check if the error is a refresh token error from Auth0
   */
  private isRefreshTokenError(error: any): boolean {
    return (
      error?.error === 'invalid_grant' && 
      error?.error_description?.includes('Unknown or invalid refresh token') &&
      error?.status === 403
    );
  }

  /**
   * Handle user logout when refresh token is invalid
   */
  private handleLogout(): void {
    // Clear auth data
    localStorage.removeItem('auth');
    localStorage.removeItem('org_id');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('roles');
    
    // Clear site-related data
    localStorage.removeItem('sites');
    localStorage.removeItem('site');
    localStorage.removeItem('types');
    localStorage.removeItem('type');
    localStorage.removeItem('statistics');
    
    // Clear session storage
    sessionStorage.removeItem('auth_in_progress');
    sessionStorage.removeItem('handling_auth');
    
    // Reset ready states
    this.tokenReadySubject.next(false);
    this.orgIdReadySubject.next(false);
    
    // Dispatch logout action
    this.store.dispatch(logout());
    
    // Navigate to login page
    this.router.navigate(['/login']);
  }

  /**
   * Wait for both token and org_id to be ready before proceeding
   */
  waitForAuth(): Observable<boolean> {
    return this.authReady$.pipe(
      take(1),
      switchMap(isReady => {
        if (isReady) {
          return of(true);
        }
        return combineLatest([
          this.getTokenWithFallback(),
          this.orgIdReady$
        ]).pipe(
          map(([token, orgIdReady]) => Boolean(token) && orgIdReady),
          catchError(() => of(false))
        );
      })
    );
  }
} 